import type { PublicConfig } from '@reditelji/shared';
import * as Sentry from '@sentry/react';
import { ApiRequestError } from '../api/client';

const EMAIL_REGEX = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
const NETWORK_ERROR_MESSAGE = /failed to fetch|networkerror|load failed|network request failed/i;

export function maskEmailString(str: string): string {
  return str.replace(EMAIL_REGEX, (match) => {
    const atIdx = match.indexOf('@');
    if (atIdx === -1) return '***';
    const local = match.slice(0, atIdx);
    const domain = match.slice(atIdx + 1);
    return `${local.length <= 1 ? '*' : `${local[0]}***`}@${domain}`;
  });
}

function scrubObject(obj: unknown): void {
  if (!obj || typeof obj !== 'object') return;
  const record = obj as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (/^(email|phone|password|token|secret|apiKey|authorization|cookie)$/i.test(key)) {
      record[key] = '[redacted]';
    } else if (typeof record[key] === 'string') {
      record[key] = maskEmailString(record[key]);
    } else if (typeof record[key] === 'object' && record[key] !== null) {
      scrubObject(record[key]);
    }
  }
}

/**
 * A dropped connection is not a bug worth an event. Marshals work the course on
 * patchy mobile data, where every failed query, background refetch and SSE
 * reconnect would otherwise become its own GlitchTip issue.
 */
export function isTransientNetworkError(error: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (!(error instanceof Error)) return false;
  if (error.name === 'AbortError' || error.name === 'TimeoutError') return true;
  // fetch() reports network failures as a TypeError; match only those messages,
  // so a genuine TypeError from our own code still gets reported.
  return error instanceof TypeError && NETWORK_ERROR_MESSAGE.test(error.message);
}

export function scrubWebEvent<T extends Sentry.ErrorEvent>(
  event: T,
  hint?: Sentry.EventHint,
): T | null {
  const err = hint?.originalException;
  if (err instanceof ApiRequestError && err.status < 500) {
    return null;
  }
  if (isTransientNetworkError(err)) {
    return null;
  }

  if (event.user) {
    delete event.user.email;
    delete event.user.username;
    delete event.user.ip_address;
  }

  if (event.extra) scrubObject(event.extra);
  if (event.contexts) scrubObject(event.contexts);

  if (event.message) {
    event.message = maskEmailString(event.message);
  }

  if (event.exception?.values) {
    for (const ex of event.exception.values) {
      // captureException puts the thrown message here, not on event.message.
      if (ex.value) ex.value = maskEmailString(ex.value);
      if (ex.type) ex.type = maskEmailString(ex.type);
    }
  }

  if (event.breadcrumbs) {
    for (const b of event.breadcrumbs) {
      if (b.message) {
        b.message = maskEmailString(b.message);
      }
      if (b.data) {
        scrubObject(b.data);
      }
    }
  }

  return event;
}

/** Cap on the config fetch, so a slow or dead API never holds up the first render. */
const INIT_TIMEOUT_MS = 3000;

/**
 * Resolves once the client is installed (or known not to be). The caller awaits
 * this before rendering: a crash during the first render is exactly what the
 * error boundary exists to report, and Sentry drops what it is handed before
 * `init`.
 */
export async function initErrorTracking(): Promise<void> {
  try {
    const signal = AbortSignal.timeout(INIT_TIMEOUT_MS);
    const res = await fetch('/api/v1/config', { signal });
    if (!res.ok) return;
    const data = (await res.json()) as PublicConfig;
    if (!data.glitchtipDsn) return;
    Sentry.init({
      dsn: data.glitchtipDsn,
      release: data.release,
      environment: data.environment,
      tracesSampleRate: 0,
      sendDefaultPii: false,
      beforeSend: scrubWebEvent,
    });
  } catch {
    // Config fetch failed or timed out - error tracking remains inactive
  }
}

export function setErrorTrackingUser(userId: string | null): void {
  if (userId) {
    Sentry.setUser({ id: userId });
  } else {
    Sentry.setUser(null);
  }
}
